/**
 * ticketLinks.js — the `$<conversationId>` link written into QA feedback.
 *
 * WHY A TEXT TOKEN AND NOT AN <a> ELEMENT
 * ---------------------------------------
 * Feedback is stored as HTML and rendered through utils/sanitize.js
 * `sanitizeHTML`, which runs DOMPurify with `ALLOW_DATA_ATTR: false`. Any
 * marker attribute — data-ticket-id and friends — is therefore STRIPPED on the
 * way to the screen, so a link cannot carry its identity in the stored markup.
 *
 * So the stored form is plain text: `$215475587353792`. Text survives every
 * sanitizer, every copy-paste, every editor round-trip and every future change
 * to the sanitize config, and it is exactly what the grader typed. The anchor is
 * built at RENDER time, after sanitizing, by decorate() below — which is also
 * why the attributes it adds are safe: nothing re-sanitizes them away, and the
 * only value interpolated is a run of digits this file matched itself.
 *
 * ORDER IS LOAD-BEARING: sanitize first, decorate second. Decorating first
 * would hand DOMPurify markup to strip; and never pass un-sanitized HTML here.
 *
 * WHAT THE LINK MEANS ON EACH SURFACE
 * -----------------------------------
 *   read-only feedback    a plain click opens that grade. Handled once, in
 *   (/scores, /goals,     <TicketContentDisplay/>, which every one of those
 *   /tl, qa-manager,      surfaces already renders its feedback through — see
 *   the review dialog)    context/LinkedGradeContext.jsx.
 *   the feedback EDITOR   no anchor at all: the innerHTML there IS the stored
 *                         value, so decoration would be written back through
 *                         onChange and the token would stop being a token. The
 *                         editor hit-tests the raw text instead and raises a
 *                         hover pill over it — TicketRichTextEditor.
 */

// `$` followed by the bare conversation id — no brackets (Filip, 2026-09-01).
//
// The digits are self-terminating, so no closing delimiter is needed; what the
// bracket bought was protection against matching money. TWELVE digits minimum
// buys the same thing more cheaply: a real Intercom conversation id is 15
// digits, while "$5", "$1200" and even "$1000000" fall short and stay prose.
// \d only, so the value interpolated into the anchor below can never be
// anything else.
//
// The BRACKETED form is the original one and is still matched, so the feedback
// written while it was the format keeps its links. Nothing writes it any more.
export const TICKET_LINK_RX = /\$(?:\[(\d{12,20})\]|(\d{12,20}))/g;

/** The id out of a TICKET_LINK_RX match, whichever of the two forms hit. */
export const matchedTicketId = (m) => m[1] || m[2];

/** Does this HTML/text contain at least one ticket link? */
export const hasTicketLinks = (html) =>
  !!html && new RegExp(TICKET_LINK_RX.source).test(String(html));

/** The token a grader's editor inserts. */
export const ticketLinkToken = (conversationId) => `$${conversationId}`;

/**
 * Turn `$<id>` tokens in ALREADY-SANITIZED html into anchors.
 *
 * Deliberately skips tokens inside an existing <a …>…</a>: a grader who pasted
 * a real URL whose text happens to contain the token should not get a link
 * nested in a link, which is invalid HTML and renders unpredictably.
 *
 * @param {string} html      output of sanitizeHTML()
 * @param {object} [opts]
 * @param {boolean} [opts.requireModifier]  true on QA edit surfaces — renders
 *        the hint that Ctrl/Cmd is needed, and marks the anchor so the shared
 *        click handler knows to ignore a plain click.
 */
export const decorateTicketLinks = (html, { requireModifier = false } = {}) => {
  if (!html) return html;
  const src = String(html);
  if (!hasTicketLinks(src)) return src;

  // Split on anchors so tokens inside one are left alone.
  const parts = src.split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi);
  return parts
    .map((part) => {
      if (/^<a\b/i.test(part)) return part;
      return part.replace(new RegExp(TICKET_LINK_RX.source, 'g'), (...m) => {
        const id = matchedTicketId(m);
        return (
        `<a href="#ticket-${id}" data-ticket-link="${id}"${requireModifier ? ' data-ticket-link-modifier="1"' : ''}` +
        ` title="${requireModifier ? 'Ctrl/Cmd + click to open this evaluation' : 'Open this evaluation'}"` +
        ` class="clara-ticket-link">#${id}</a>`
        );
      });
    })
    .join('');
};

/**
 * The one click rule, shared by both surfaces so they cannot drift.
 * Returns the conversation id to open, or null to let the event through.
 *
 * @param {MouseEvent} e  a click on (or inside) the rendered feedback
 */
export const ticketLinkTarget = (e) => {
  const el = e.target?.closest?.('[data-ticket-link]');
  if (!el) return null;
  const needsModifier = el.getAttribute('data-ticket-link-modifier') === '1';
  // A modifier-gated link is inert on a plain click — including no
  // preventDefault, so the caret lands where the grader clicked.
  if (needsModifier && !(e.ctrlKey || e.metaKey)) return null;
  e.preventDefault();
  e.stopPropagation();
  return el.getAttribute('data-ticket-link');
};
